import { Combine, Layers, MapPin } from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { GlobeCorner } from './components/Globe';
import { LayerLegend } from './components/LayerLegend';
import { VenueMap } from './components/VenueMap';
import { useAnalyticsClient } from './hooks/useAnalyticsClient';
import { useVenueOS } from './hooks/useVenueOS';
import { useVenues } from './hooks/useVenues';
import type { Venue } from './lib/map-helpers';
import { storage } from './lib/storage';
import { cn } from './lib/utils';
import { useAppStore } from './state/app-store';

const IGNORE_TERMS = ['test', 'clone', 'staging'];
const INTRO_KEY = 'intro-shown-date';

function isRealVenue(v: Venue): boolean {
  const n = (v.name || '').toLowerCase();
  return !IGNORE_TERMS.some((t) => n.includes(t));
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );
  useEffect(() => {
    const c = (): void => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', c);
    return () => window.removeEventListener('resize', c);
  }, []);
  return mobile;
}

function StackToggle(): React.JSX.Element | null {
  const isStacked = useAppStore((s) => s.isStacked);
  const floors = useAppStore((s) => s.floors);

  const levelCount = useMemo(() => {
    const seen: Record<number, boolean> = {};
    for (const f of floors) seen[f.elevation ?? 0] = true;
    return Object.keys(seen).length;
  }, [floors]);

  const toggle = useCallback(() => {
    const store = useAppStore.getState();
    const next = !store.isStacked;
    store.setIsStacked(next);
    setTimeout(() => useAppStore.getState().applyStackedView(), 50);
  }, []);

  if (!floors.length || levelCount <= 1) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      title={isStacked ? 'Collapse floors' : 'Expand floors'}
      className={cn(
        'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all',
        'backdrop-blur-md border shadow-sm',
      )}
      style={{
        background: isStacked ? 'rgba(99,102,241,0.15)' : 'rgba(14,19,32,0.7)',
        borderColor: isStacked ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.08)',
        color: isStacked ? '#a5b4fc' : '#94a3b8',
      }}
    >
      {isStacked ? <Layers className="w-3.5 h-3.5" /> : <Combine className="w-3.5 h-3.5" />}
      <span>{isStacked ? 'Stacked' : 'Flat'}</span>
    </button>
  );
}

export function App(): React.JSX.Element {
  const {
    client,
    context,
    isEmbedded,
    isLoading: venueosLoading,
    error: venueosError,
  } = useVenueOS();
  const { analyticsClient } = useAnalyticsClient({ venueOSClient: client });
  const { venues } = useVenues({ client, context });
  const appMode = useAppStore((s) => s.appMode);
  const selectedVenue = useAppStore((s) => s.selectedVenue);
  const showVenueMap = useAppStore((s) => s.showVenueMap);
  const [ready, setReady] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    useAppStore.getState().setVenues(venues);
  }, [venues]);

  // If the VenueOS host has a selected venue (`context.map`), match it
  // against our fetched venue list and jump straight to the venue view.
  // This follows the host selection both on initial load and whenever
  // the user switches venues in VenueOS.
  const hostMapId = context?.map?.id ?? null;

  useEffect(() => {
    if (!client || !context) return;
    if (!hostMapId) return;
    if (venues.length === 0) return;
    const match = venues.find((v) => v.id === hostMapId);
    if (!match) return;
    const current = useAppStore.getState().selectedVenue;
    if (current?.id === match.id) {
      if (!ready) setReady(true);
      return;
    }
    useAppStore.setState({ appMode: 'venue', selectedVenue: match });
    setReady(true);
    setTimeout(() => useAppStore.getState().pickVenue(match, { skipGlobe: true }), 50);
  }, [client, context, hostMapId, venues, ready]);

  useEffect(() => {
    if (!client || !context) return;
    if (ready) return;
    if (hostMapId) return;

    const allVenues = venues;
    const real = allVenues.filter(isRealVenue);
    const solo = real.length === 1 ? (real[0] ?? null) : null;
    const today = todayStr();

    void Promise.all([
      storage.get<string>(INTRO_KEY),
      storage.get<string>('selected-venue-id'),
    ]).then(([seen, savedId]) => {
      const seenToday = seen === today;
      const savedVenue =
        savedId && typeof savedId === 'string'
          ? (allVenues.find((v) => v.id === savedId) ?? null)
          : null;

      if (solo && !seenToday && !isMobile) {
        void storage.set(INTRO_KEY, today);
        setReady(true);
        setTimeout(() => useAppStore.getState().pickVenue(solo), 2200);
      } else if (savedVenue || solo) {
        const pick = savedVenue ?? solo;
        if (pick) {
          useAppStore.setState({ appMode: 'venue', selectedVenue: pick });
          setReady(true);
          setTimeout(() => useAppStore.getState().pickVenue(pick, { skipGlobe: true }), 50);
        } else {
          setReady(true);
        }
      } else {
        setReady(true);
      }
    });
  }, [client, context, venues, ready, isMobile, hostMapId]);

  if (venueosLoading) {
    return (
      <div
        className="h-screen w-screen flex items-center justify-center"
        style={{ background: '#0e1320' }}
      >
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isEmbedded) {
    return (
      <div
        className="h-screen w-screen flex items-center justify-center px-8 text-center"
        style={{ background: '#0e1320' }}
      >
        <div>
          <h1 className="text-xl font-semibold mb-2 text-foreground">Venue Overview</h1>
          <p className="text-sm text-muted-foreground max-w-md">
            This applet must be embedded inside a VenueOS host to load venue data.
          </p>
        </div>
      </div>
    );
  }

  if (venueosError) {
    return (
      <div
        className="h-screen w-screen flex items-center justify-center px-8 text-center"
        style={{ background: '#0e1320' }}
      >
        <div>
          <h1 className="text-lg font-semibold text-destructive mb-1">VenueOS init failed</h1>
          <p className="text-sm text-muted-foreground">{venueosError}</p>
        </div>
      </div>
    );
  }

  const showMap = showVenueMap && selectedVenue !== null;

  if (!ready) {
    return (
      <div
        className="h-screen w-screen flex items-center justify-center"
        style={{ background: '#0e1320' }}
      >
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Fragment>
      <div
        className="h-screen w-screen flex overflow-hidden"
        style={{
          background: 'transparent',
          opacity: showMap ? 1 : 0,
          pointerEvents: showMap ? 'auto' : 'none',
          transition: 'opacity 0.6s ease',
        }}
      >
        <div className="flex-1 min-w-0 relative">
          {showMap && <VenueMap client={client} />}
          {showMap && <LayerLegend client={client} analyticsClient={analyticsClient} />}

          {showMap && (
            <div
              className="absolute z-20 flex flex-col items-end gap-1.5"
              style={isMobile ? { top: 10, right: 10 } : { top: 12, right: 12 }}
            >
              <StackToggle />
            </div>
          )}
        </div>
      </div>

      {appMode === 'globe' && <SelectVenuePrompt />}

      {!isMobile && <GlobeCorner />}
    </Fragment>
  );
}

function SelectVenuePrompt(): React.JSX.Element {
  return (
    <div
      className="fixed left-1/2 z-[10000] pointer-events-none"
      style={{ bottom: 24, transform: 'translateX(-50%)' }}
    >
      <div
        className="pointer-events-auto flex items-center gap-2 rounded-full px-4 py-2"
        style={{
          background: 'rgba(14,19,32,0.82)',
          backdropFilter: 'blur(16px) saturate(1.4)',
          border: '1px solid rgba(99,102,241,0.22)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}
      >
        <MapPin className="w-3.5 h-3.5 text-indigo-300" strokeWidth={2} />
        <span className="text-[11px] font-medium text-foreground/90 whitespace-nowrap">
          Select a venue to get started
        </span>
      </div>
    </div>
  );
}
